import { formatRelative } from '../../utils/time.js';
import Icon from '../common/Icon.jsx';

/**
 * NewsCard — single article tile in the feed.
 *
 * Two visual variants are produced by the same component, picked by the
 * caller via the `variant` prop:
 *   - 'hero'    full-bleed cover image, larger type; used for the top item
 *   - 'compact' side-by-side thumbnail + text; used for the rest of the list
 *
 * The accent colour rail on the left is the team livery (or category accent)
 * — tying every article visually to its subject without leaning on the
 * cover image alone.
 *
 * `onOpen` is wired through to the parent so the screen can route to the
 * detail view (not yet built) or seed the Oracle. Keeping the click handler
 * in props rather than the component keeps the card pure.
 */
export default function NewsCard({ article, variant = 'compact', onOpen }) {
  if (variant === 'hero') {
    return (
      <article
        onClick={() => onOpen?.(article)}
        className="pressable group relative mb-3 overflow-hidden rounded-2xl bg-bg-card ring-1 ring-stroke shadow-card cursor-pointer"
      >
        <div className="relative h-48 w-full overflow-hidden">
          {article.coverImage ? (
            <img
              src={article.coverImage}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-bg-card2 to-bg-base" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
          {article.isBreaking ? (
            <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-f1-red/95 px-3 py-1 text-2xs font-bold uppercase tracking-widest-plus text-white shadow-glow-red">
              <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
              Breaking
            </div>
          ) : null}
        </div>
        <div className="relative -mt-12 px-5 pb-5">
          <div className="mb-2 flex items-center gap-2 text-2xs font-semibold uppercase tracking-widest-plus text-ink-secondary">
            <span style={{ color: article.accentColor }}>● {article.source}</span>
            <span className="text-ink-muted">•</span>
            <span>{article.dateEstimated ? 'Recent' : formatRelative(article.publishedAt)}</span>
          </div>
          <h2 className="text-lg font-bold leading-snug text-ink-primary">{article.title}</h2>
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-ink-secondary">
            {article.summary}
          </p>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-2xs text-ink-tertiary">{article.readTime} min read</span>
            <span className="inline-flex items-center gap-1 text-2xs font-semibold uppercase tracking-widest-plus text-f1-red">
              Read
              <Icon name="chevronRight" size={12} />
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      onClick={() => onOpen?.(article)}
      className="pressable group relative mb-3 flex gap-3 overflow-hidden rounded-2xl bg-bg-card p-3 ring-1 ring-stroke transition-all hover:bg-bg-card2 hover:shadow-card-hover cursor-pointer"
    >
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 h-full w-[3px]"
        style={{ backgroundColor: article.accentColor }}
      />
      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-bg-card2">
        {article.coverImage ? (
          <img
            src={article.coverImage}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-bg-card2 to-bg-base" />
        )}
        {article.isBreaking ? (
          <div className="absolute right-1 top-1 inline-flex items-center rounded-full bg-f1-red px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
            !
          </div>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest-plus text-ink-tertiary">
          <span style={{ color: article.accentColor }} className="truncate">
            {article.source}
          </span>
          <span className="text-ink-muted">•</span>
          <span>{article.dateEstimated ? 'Recent' : formatRelative(article.publishedAt)}</span>
        </div>
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-ink-primary">
          {article.title}
        </h3>
        <p className="mt-1 line-clamp-1 text-xs leading-relaxed text-ink-tertiary">
          {article.summary}
        </p>
      </div>
    </article>
  );
}
